/**
 * 批处理（M4-04 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 对一组打开的文件依次：解码→建单图层文档→在一条全新 CommandBus 上回放
 * 录制的动作→压平→编码→导出。每个文件独立失败：一个文件回放/编码出错
 * 不该拖累其余文件——批处理的价值就在"跑一批，出结果时看哪些失败了"，
 * 中途整体中止反而是更差的体验。
 */
import { CommandBus } from './command-bus';
import { compositeDocument } from './composite';
import { documentFromImage } from './file/image-import';
import { replayAction, type RecordedAction } from './actions/action-recorder';
import { decodeImage, encodeImage, type ImageFormat } from '../io/codecs';
import type { FileGateway, OpenedFile } from '../io/gateway';
import type { TileStore } from '../engine/tile/store';
import { layerId } from '../engine/tile/key';

export interface BatchDeps {
  readonly tiles: TileStore;
  readonly gateway: FileGateway;
  readonly now: () => string;
}

export interface BatchOptions {
  readonly action: RecordedAction;
  readonly outputFormat: ImageFormat;
  readonly quality?: number;
}

export interface BatchFileResult {
  readonly name: string;
  readonly ok: boolean;
  readonly error?: string;
}

/** 每处理完一个文件调用一次（成败都调用），用于驱动进度 UI。 */
export type BatchProgressCallback = (done: number, total: number, latest: BatchFileResult) => void;

const MIME: Readonly<Record<ImageFormat, string>> = { png: 'image/png', jpeg: 'image/jpeg' };
const EXT: Readonly<Record<ImageFormat, string>> = { png: '.png', jpeg: '.jpg' };

function outputName(sourceName: string, format: ImageFormat): string {
  const stem = sourceName.replace(/\.[^.]+$/, '');
  return `${stem}${EXT[format]}`;
}

/** 处理一批已打开的文件，返回逐文件结果（不抛出——单个文件失败不中止批处理）。 */
export async function runBatch(
  deps: BatchDeps,
  files: readonly OpenedFile[],
  options: BatchOptions,
  signal?: AbortSignal,
  onProgress?: BatchProgressCallback,
): Promise<BatchFileResult[]> {
  const results: BatchFileResult[] = [];
  for (let i = 0; i < files.length; i++) {
    if (signal?.aborted === true) {
      results.push({ name: files[i]!.name, ok: false, error: '已中止' });
      onProgress?.(results.length, files.length, results[results.length - 1]!);
      continue;
    }
    const file = files[i]!;
    const docId = `batch-${i}-${deps.now()}`;
    try {
      const image = await decodeImage(file.bytes, signal === undefined ? {} : { signal });
      const document = documentFromImage(image, deps.tiles, {
        id: docId,
        fileName: file.name,
        now: deps.now,
      });
      const bus = new CommandBus({ document, now: deps.now });
      replayAction((command) => bus.dispatch(command), options.action);
      const composite = compositeDocument(bus.document, deps.tiles);
      const bytes = await encodeImage(options.outputFormat, composite.pixels, {
        width: composite.width,
        height: composite.height,
        ...(options.quality === undefined ? {} : { quality: options.quality }),
        ...(signal === undefined ? {} : { signal }),
      });
      await deps.gateway.save({
        name: outputName(file.name, options.outputFormat),
        bytes,
        mimeType: MIME[options.outputFormat],
      });
      results.push({ name: file.name, ok: true });
    } catch (error) {
      results.push({
        name: file.name,
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      // 这张图的瓦片只在本次批处理里用得上，处理完（无论成败）立刻释放，
      // 否则批处理跑到第 N 张时 TileStore 里还堆着前 N-1 张的像素
      deps.tiles.dropLayer(layerId(`${docId}:bg`));
      onProgress?.(results.length, files.length, results[results.length - 1]!);
    }
  }
  return results;
}
